/**
 * Web Vitals Performance Monitoring
 *
 * This module tracks Core Web Vitals and sends them to Sentry for monitoring.
 *
 * Core Web Vitals Tracked:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Loading performance
 * - LCP (Largest Contentful Paint): Loading performance
 * - TTFB (Time to First Byte): Server response time
 * - INP (Interaction to Next Paint): Responsiveness
 *
 * Metrics are sent to Sentry as custom measurements for performance tracking.
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

/**
 * Send Web Vitals metric to Sentry
 */
function sendToSentry(metric: Metric) {
  // Don't send metrics in development
  if (ENVIRONMENT === 'development') {
    console.log('[Web Vitals]', metric.name, metric.value, metric);
    return;
  }

  // Send to Sentry as a measurement
  Sentry.setMeasurement(metric.name, metric.value, metric.rating);

  // Also capture as breadcrumb for context
  Sentry.addBreadcrumb({
    category: 'web-vitals',
    message: `${metric.name}: ${metric.value}`,
    data: {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
    },
    level: 'info',
  });

  // Log critical performance issues
  if (metric.rating === 'poor') {
    console.warn(`[Web Vitals] Poor ${metric.name}:`, metric.value);

    // Capture poor performance as Sentry message
    Sentry.captureMessage(
      `Poor Web Vital: ${metric.name} = ${metric.value}`,
      {
        level: 'warning',
        extra: {
          metric: metric.name,
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        },
      }
    );
  }
}

/**
 * Initialize Web Vitals tracking
 * Call this function to start monitoring Core Web Vitals
 */
export function reportWebVitals() {
  // Track all Core Web Vitals
  onCLS(sendToSentry);  // Cumulative Layout Shift
  onFCP(sendToSentry);  // First Contentful Paint
  onLCP(sendToSentry);  // Largest Contentful Paint
  onTTFB(sendToSentry); // Time to First Byte
  onINP(sendToSentry);  // Interaction to Next Paint (replaces deprecated FID)

  console.log('✅ Web Vitals tracking initialized');
}

/**
 * Get Web Vitals thresholds
 * These are the standard thresholds defined by Google
 */
export const WEB_VITALS_THRESHOLDS = {
  CLS: { good: 0.1, needsImprovement: 0.25 },      // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 },     // First Contentful Paint (ms)
  LCP: { good: 2500, needsImprovement: 4000 },     // Largest Contentful Paint (ms)
  TTFB: { good: 800, needsImprovement: 1800 },     // Time to First Byte (ms)
  INP: { good: 200, needsImprovement: 500 },       // Interaction to Next Paint (ms)
};

export default reportWebVitals;
